/**
 * Background sync: flushes the local outbox to the backend API whenever the
 * app is online and a token is configured. The app is fully usable without
 * this ever succeeding — every write already landed in IndexedDB first.
 */
import { getOutbox, removeOutboxEntry, getMeta, setMeta, outboxCount } from '../db/indexeddb';
import type { OutboxEntry } from './types';

export type SyncStatus = 'offline' | 'online-unsynced' | 'syncing' | 'synced' | 'error' | 'no-token';

let listeners: Array<(status: SyncStatus, pending: number) => void> = [];
let currentStatus: SyncStatus = 'offline';

export function onSyncStatus(cb: (status: SyncStatus, pending: number) => void): () => void {
  listeners.push(cb);
  return () => { listeners = listeners.filter((l) => l !== cb); };
}

async function notify(status: SyncStatus) {
  currentStatus = status;
  const pending = await outboxCount();
  for (const l of listeners) l(status, pending);
}

export function getCurrentStatus(): SyncStatus {
  return currentStatus;
}

export async function getApiConfig(): Promise<{ baseUrl: string; token: string } | null> {
  const baseUrl = (await getMeta('api_base_url')) as string | undefined;
  const token = (await getMeta('api_token')) as string | undefined;
  if (!baseUrl || !token) return null;
  return { baseUrl, token };
}

export async function setApiConfig(baseUrl: string, token: string): Promise<void> {
  await setMeta('api_base_url', baseUrl);
  await setMeta('api_token', token);
}

async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const cfg = await getApiConfig();
  if (!cfg) throw new Error('no-token');
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${cfg.token}`);
  if (init.body) headers.set('Content-Type', 'application/json');
  return fetch(`${cfg.baseUrl.replace(/\/$/, '')}${path}`, { ...init, headers });
}

async function applyOp(op: OutboxEntry['op']): Promise<void> {
  switch (op.kind) {
    case 'create': {
      const r = await apiFetch('/api/records', { method: 'POST', body: JSON.stringify(op.record) });
      if (!r.ok && r.status !== 409) throw new Error(`create failed: ${r.status}`);
      return;
    }
    case 'update': {
      const r = await apiFetch(`/api/records/${op.id}`, { method: 'PATCH', body: JSON.stringify(op.patch) });
      if (!r.ok) throw new Error(`update failed: ${r.status}`);
      return;
    }
    case 'archive': {
      const r = await apiFetch(`/api/records/${op.id}/archive`, { method: 'POST' });
      if (!r.ok) throw new Error(`archive failed: ${r.status}`);
      return;
    }
    case 'restore': {
      const r = await apiFetch(`/api/records/${op.id}/restore`, { method: 'POST' });
      if (!r.ok) throw new Error(`restore failed: ${r.status}`);
      return;
    }
  }
}

let flushing = false;

export async function flushOutbox(): Promise<void> {
  if (flushing) return;
  const cfg = await getApiConfig();
  if (!cfg) { await notify('no-token'); return; }
  if (!navigator.onLine) { await notify('offline'); return; }

  flushing = true;
  await notify('syncing');
  try {
    const entries = await getOutbox();
    for (const entry of entries) {
      try {
        await applyOp(entry.op);
        if (entry.seq !== undefined) await removeOutboxEntry(entry.seq);
      } catch (err) {
        // Stop on first failure (likely offline mid-flush or server down) —
        // remaining entries stay queued for the next attempt, in order.
        console.warn('gvm sync: op failed, will retry later', err);
        await notify('error');
        flushing = false;
        return;
      }
    }
    await notify('synced');
  } finally {
    flushing = false;
  }
}

export function initSync(): void {
  window.addEventListener('online', () => { void flushOutbox(); });
  window.addEventListener('offline', () => { void notify('offline'); });
  void (async () => {
    if (!navigator.onLine) { await notify('offline'); return; }
    await flushOutbox();
  })();
  // Periodic retry in case the first flush hit a transient error.
  setInterval(() => { void flushOutbox(); }, 30_000);
}
